import Transform from '../math/Transform.js';
import DOMUtils from '../utils/DOMUtils.js';

/**
 * GroundEffect - A sortable ground-level visual effect (circles, markers, etc.)
 *
 * Unlike overlays that use z-index, GroundEffects participate in the World's
 * SAT-based sorting system, allowing them to be properly occluded by cuboids
 * and appear at the correct depth relative to other world objects.
 *
 * Use cases:
 * - Destination markers (click-to-move)
 * - NPC/character selection circles
 * - Spell/ability ground indicators
 * - Area-of-effect visualizations
 */
export default class GroundEffect {
  // Default effect parameters
  static DEFAULT_RADIUS = 0.5;        // World units
  static DEFAULT_HEIGHT = 0.02;       // Thin disc for sorting purposes
  static DEFAULT_COLOR = { r: 50, g: 200, b: 100 };
  static DEFAULT_OPACITY = 0.8;

  /**
   * Create a new ground effect
   * @param {Object} options - Effect options
   * @param {number} options.x - World X position
   * @param {number} options.y - World Y position (surface height)
   * @param {number} options.z - World Z position
   * @param {number} [options.radius=0.5] - Effect radius in world units
   * @param {Object} [options.color] - RGB color {r, g, b}
   * @param {number} [options.opacity=0.8] - Base opacity (0-1)
   * @param {string} [options.animation] - CSS animation name (e.g., 'pulse')
   * @param {number} [options.cubeSize=60] - Cube size for pixel conversion
   */
  constructor(options = {}) {
    this.x = options.x || 0;
    this.y = options.y || 0;
    this.z = options.z || 0;
    this.radius = options.radius || GroundEffect.DEFAULT_RADIUS;
    this.color = options.color || { ...GroundEffect.DEFAULT_COLOR };
    this.opacity = options.opacity || GroundEffect.DEFAULT_OPACITY;
    this.animation = options.animation || 'pulse';
    this.cubeSize = options.cubeSize || 60;
    this.label = options.label || null; // Optional text label (e.g., waypoint number)

    // For sorting: treat as a thin disc
    this.height = GroundEffect.DEFAULT_HEIGHT;
    this.width = this.radius * 2;
    this.depth = this.radius * 2;

    // Unique ID for tracking
    this.id = `ground-effect-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // DOM element
    this.element = null;

    // World reference (set when added to world)
    this.world = null;

    // Visibility state
    this.visible = false;

    // Fade state - managed manually because DOM re-parenting interrupts CSS transitions
    this._fading = false;
    this._fadeStartTime = 0;
    this._fadeDuration = 0;

    // Create the DOM element
    this._createElement();
  }

  /**
   * Create the DOM element for this effect
   * @private
   */
  _createElement() {
    const el = document.createElement('div');
    el.className = 'ground-effect';
    el.dataset.effectId = this.id;

    // Base size in pixels (will be scaled by world zoom)
    const sizePx = this.radius * 2 * this.cubeSize;

    Object.assign(el.style, {
      position: 'absolute',
      width: `${sizePx}px`,
      height: `${sizePx}px`,
      pointerEvents: 'none',
      display: 'none',
      // Transform origin at center for proper isometric squash
      transformOrigin: 'center center'
    });

    // Create SVG with animated rings
    this._createRingContent(el, sizePx);
    this.element = el;
  }

  /**
   * Create animated ring content using CSS-animated div elements
   * Uses time-synced negative delays so animations persist through DOM re-parenting
   * @param {HTMLElement} el - Container element
   * @param {number} sizePx - Size in pixels
   * @param {number} zoom - Zoom level for scaling visual properties (default 1)
   * @private
   */
  _createRingContent(el, sizePx, zoom = 1) {
    const { r, g, b } = this.color;
    const opacity = this.opacity;

    // Animation timing - 2 rings with staggered contraction
    const numRings = 2;
    const ringDuration = 1.2; // seconds per ring cycle

    // Calculate time-synced delays so animations appear continuous
    // even when elements are removed/re-added to DOM during sorting
    const now = performance.now() / 1000; // current time in seconds

    // Scale visual properties by zoom (on Android, zoom is baked in)
    const borderWidth = 6 * zoom;
    const blurAmount = 2 * zoom;
    const glowSize1 = 8 * zoom;
    const glowSize2 = 16 * zoom;
    const innerGlow = 6 * zoom;

    // Create rings as absolutely positioned divs
    for (let i = 0; i < numRings; i++) {
      const ring = document.createElement('div');
      ring.className = 'ground-effect-ring';

      // Each ring is offset by half the duration for nice stagger
      const ringOffset = (i / numRings) * ringDuration;
      // Calculate negative delay to sync with real time
      const timeInCycle = (now + ringOffset) % ringDuration;
      const syncedDelay = -timeInCycle;

      Object.assign(ring.style, {
        position: 'absolute',
        left: '0',
        top: '0',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        // Thick diffuse band - uses thick border + blur for soft edges
        border: `${borderWidth}px solid rgba(${r}, ${g}, ${b}, ${opacity * 0.7})`,
        boxSizing: 'border-box',
        // Blur the ring itself for diffuse band effect
        filter: `blur(${blurAmount}px)`,
        // Inner and outer glow for extra diffusion
        boxShadow: `0 0 ${glowSize1}px rgba(${r}, ${g}, ${b}, 0.8), 0 0 ${glowSize2}px rgba(${r}, ${g}, ${b}, 0.4), inset 0 0 ${innerGlow}px rgba(${r}, ${g}, ${b}, 0.3)`,
        // Smooth gravitational pull
        animation: `ground-effect-ring-contract ${ringDuration}s ease-in ${syncedDelay}s infinite`
      });

      el.appendChild(ring);
    }

    // Create center "gravity well" that pulses inward when rings arrive
    // Rings arrive every ringDuration/numRings = 0.6s
    const dotPulseDuration = ringDuration / numRings;
    const dotSize = sizePx * 0.25; // Larger center for visibility
    const centerDot = document.createElement('div');
    centerDot.className = 'ground-effect-center';

    const dotTimeInCycle = now % dotPulseDuration;
    const dotSyncedDelay = -dotTimeInCycle;

    // Scale glow sizes for center dot
    const dotGlow1 = 15 * zoom;
    const dotGlow2 = 25 * zoom;

    Object.assign(centerDot.style, {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: `${dotSize}px`,
      height: `${dotSize}px`,
      marginLeft: `${-dotSize / 2}px`,
      marginTop: `${-dotSize / 2}px`,
      borderRadius: '50%',
      // Radial gradient for soft diffuse "gravity well" look
      background: `radial-gradient(circle, rgba(${r}, ${g}, ${b}, ${opacity * 0.9}) 0%, rgba(${r}, ${g}, ${b}, 0.5) 40%, rgba(${r}, ${g}, ${b}, 0.1) 70%, rgba(${r}, ${g}, ${b}, 0) 100%)`,
      // Glow effect
      boxShadow: `0 0 ${dotGlow1}px rgba(${r}, ${g}, ${b}, 0.5), 0 0 ${dotGlow2}px rgba(${r}, ${g}, ${b}, 0.25)`,
      // "Pull in" animation synced to ring arrivals
      animation: `ground-effect-dot-pulse ${dotPulseDuration}s ease-in-out ${dotSyncedDelay}s infinite`
    });

    el.appendChild(centerDot);

    // Optional text label (e.g., waypoint number)
    if (this.label) {
      const labelEl = document.createElement('div');
      labelEl.className = 'ground-effect-label';
      const labelSize = sizePx * 0.45;
      Object.assign(labelEl.style, {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${labelSize}px`,
        height: `${labelSize}px`,
        lineHeight: `${labelSize}px`,
        textAlign: 'center',
        fontSize: `${Math.max(10, labelSize * 0.65)}px`,
        fontWeight: '700',
        fontFamily: 'var(--font-primary)',
        color: '#fff',
        textShadow: '0 1px 3px rgba(0,0,0,0.8)',
        pointerEvents: 'none',
        zIndex: '1'
      });
      labelEl.textContent = this.label;
      el.appendChild(labelEl);
    }
  }

  /**
   * Update visual styling (call after changing color/opacity)
   * @private
   */
  _updateVisualStyle() {
    if (!this.element) return;

    // Clear and recreate ring content
    this.element.innerHTML = '';
    const sizePx = this.radius * 2 * this.cubeSize;
    this._createRingContent(this.element, sizePx);
  }

  /**
   * Attach to a parent element (usually #world)
   * @param {HTMLElement} parent - Parent element
   */
  attachTo(parent) {
    if (this.element && parent) {
      parent.appendChild(this.element);
    }
  }

  /**
   * Detach from DOM
   */
  detach() {
    if (this.element && this.element.parentElement) {
      this.element.parentElement.removeChild(this.element);
    }
  }

  /**
   * Set position in world coordinates
   * @param {number} x - World X
   * @param {number} y - World Y (surface height)
   * @param {number} z - World Z
   */
  setPosition(x, y, z) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  /**
   * Show the effect
   */
  show() {
    if (this.element) {
      // Cancel any fade in progress
      this._fading = false;
      this._fadeStartTime = 0;
      this._fadeDuration = 0;
      // Reset opacity and show
      this.element.style.opacity = '1';
      this.element.style.display = 'block';
      this.visible = true;
    }
  }

  /**
   * Hide the effect
   */
  hide() {
    if (this.element) {
      this.element.style.display = 'none';
      this.element.style.opacity = '1'; // Reset for next show
      this.visible = false;
      this._fading = false;
    }
  }

  /**
   * Update the effect's visual transform based on camera
   * Called each frame by World.sortAndRenderCuboids
   * @param {Object} camera - Camera object
   */
  updateTransform(camera) {
    if (!this.element || !this.visible) return;

    // On Android, zoom is baked into positions (no CSS scale on #world)
    // On other platforms, use base size and let CSS scale handle zoom
    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const CUBE_SIZE = camera.CUBE_SIZE * zoom;

    // Handle fade animation manually (CSS transitions don't survive DOM re-parenting)
    if (this._fading) {
      const elapsed = performance.now() - this._fadeStartTime;
      const progress = Math.min(elapsed / this._fadeDuration, 1);
      const opacity = 1 - progress; // Linear fade from 1 to 0

      this.element.style.opacity = opacity.toString();

      // Fade complete - hide the element
      if (progress >= 1) {
        this._fading = false;
        this.hide();
        return;
      }
    }

    // Convert world position to pixel coordinates
    const pixelX = this.x * CUBE_SIZE;
    const pixelY = this.y * CUBE_SIZE;
    const pixelZ = -this.z * CUBE_SIZE; // Negate Z for coordinate system

    // Get screen position (scale camera position on Android)
    const screenPos = Transform.worldToScreen(pixelX, pixelY, pixelZ, camera, zoom);

    // Apply the same rendering Y offset that Cuboid uses (CUBE_SIZE / 2)
    const renderingYOffset = CUBE_SIZE / 2;

    // Calculate isometric Y scale (squash for floor appearance)
    const yScale = Math.abs(Transform.calculateYScale(camera.skewAngle));

    // Position element center at the screen position
    // Use left/top for position, transform for centering and isometric squash
    this.element.style.left = `${screenPos.x}px`;
    this.element.style.top = `${screenPos.y + renderingYOffset}px`;

    // On Android, apply inverse scale to compensate for baked zoom in positions
    // This keeps the visual size consistent while positioning correctly
    if (isAndroid) {
      this.element.style.transform = `translate(-50%, -50%) scaleY(${yScale}) scale(${zoom})`;
    } else {
      this.element.style.transform = `translate(-50%, -50%) scaleY(${yScale})`;
    }

    // Keep label text upright despite isometric squash
    if (this.label) {
      const labelEl = this.element.querySelector('.ground-effect-label');
      if (labelEl && yScale > 0) {
        labelEl.style.transform = `translate(-50%, -50%) scaleY(${1 / yScale})`;
      }
    }

    // Recreate ring content with time-synced animations
    // This ensures animations stay in sync even when element is re-parented during sorting
    this._syncAnimations();
  }

  /**
   * Sync ring animations to real time
   * Called each frame to keep animations continuous despite DOM re-parenting
   * @private
   */
  _syncAnimations() {
    const rings = this.element.querySelectorAll('.ground-effect-ring');
    const centerDot = this.element.querySelector('.ground-effect-center');

    const now = performance.now() / 1000;
    const ringDuration = 1.2;
    const numRings = rings.length;

    // Update ring animation delays
    rings.forEach((ring, i) => {
      const ringOffset = (i / numRings) * ringDuration;
      const timeInCycle = (now + ringOffset) % ringDuration;
      const syncedDelay = -timeInCycle;
      ring.style.animationDelay = `${syncedDelay}s`;
    });

    // Update center dot animation delay - synced to ring arrivals
    if (centerDot) {
      const dotPulseDuration = ringDuration / numRings; // 0.6s
      const dotTimeInCycle = now % dotPulseDuration;
      const dotSyncedDelay = -dotTimeInCycle;
      centerDot.style.animationDelay = `${dotSyncedDelay}s`;
    }
  }


  /**
   * Fade out the effect smoothly, letting current animations complete
   * Uses manual opacity animation because CSS transitions don't survive DOM re-parenting
   * @param {number} duration - Fade duration in ms (default: 600)
   */
  fadeOut(duration = 600) {
    if (!this.element || !this.visible || this._fading) return;

    // Start fade animation - will be processed in updateTransform()
    this._fading = true;
    this._fadeStartTime = performance.now();
    this._fadeDuration = duration;
  }

  /**
   * Get bounding box for sorting (in world units)
   * @returns {Object} Bounding box {minX, maxX, minY, maxY, minZ, maxZ}
   */
  getBoundingBox() {
    return {
      minX: this.x - this.radius,
      maxX: this.x + this.radius,
      minY: this.y,
      maxY: this.y + this.height,
      minZ: this.z - this.radius,
      maxZ: this.z + this.radius
    };
  }

  /**
   * Get position data for World sorting system
   * Returns an object compatible with the SAT-based sorting algorithm
   * @returns {Object} Position data for sorting
   */
  getSortPosition() {
    const halfWidth = this.radius;
    const halfDepth = this.radius;

    return {
      groundEffect: this,
      element: this.element,
      x: this.x - halfWidth,
      y: this.y,
      z: this.z - halfDepth,
      width: this.width,
      height: this.height,
      depth: this.depth,
      minX: this.x - halfWidth,
      maxX: this.x + halfWidth,
      minY: this.y,
      maxY: this.y + this.height,
      minZ: this.z - halfDepth,
      maxZ: this.z + halfDepth,
      id: this.id,
      isFragment: false,
      isSprite: false,
      isGroundEffect: true,
      parentKey: this.id
    };
  }

  /**
   * Update color
   * @param {Object} color - RGB color {r, g, b}
   */
  setColor(color) {
    this.color = color;
    this._updateVisualStyle();
  }

  /**
   * Update opacity
   * @param {number} opacity - Opacity (0-1)
   */
  setOpacity(opacity) {
    this.opacity = opacity;
    this._updateVisualStyle();
  }

  /**
   * Update label text
   * @param {string|null} text
   */
  setLabel(text) {
    this.label = text;
    this._updateVisualStyle();
  }

  /**
   * Update radius
   * @param {number} radius - New radius in world units
   */
  setRadius(radius) {
    this.radius = radius;
    this.width = radius * 2;
    this.depth = radius * 2;

    if (this.element) {
      const sizePx = radius * 2 * this.cubeSize;
      this.element.style.width = `${sizePx}px`;
      this.element.style.height = `${sizePx}px`;
      this._updateVisualStyle();
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.detach();
    this.element = null;
    this.world = null;
  }
}
